import { logger } from "cayo.ui";

const log = logger.getLogger("ModelExpressionEvaluator");
const MODEL_PREFIX = "model.";
const MODEL_PREFIX_LENGTH = MODEL_PREFIX.length;

type ModelValue = string | number | boolean | null | undefined;

export class ModelExpressionEvaluator {
  /**
   * Evaluates and applies an assignment expression to a model
   * @param model The model object to modify
   * @param expression The expression in format "path=value" (e.g. "model.property=true")
   * @returns The modified model
   */
  static evaluate(model: any, expression: string): any {
    try {
      // Split the expression into path and value parts
      const [path, valueStr] = expression.split("=").map((s) => s.trim());

      if (!path || valueStr === undefined) {
        throw new Error(`Invalid expression format: ${expression}. Expected format: "path=value"`);
      }

      // Remove 'model.' prefix if present
      const normalizedPath = path.startsWith(MODEL_PREFIX) ? path.slice(MODEL_PREFIX_LENGTH) : path;

      // Parse the value string into appropriate type
      const value = ModelExpressionEvaluator.parseValue(valueStr);

      // Set the value in the model
      ModelExpressionEvaluator.setValueByPath(model, normalizedPath, value);
      return { name: normalizedPath, value };
    } catch (error) {
      log.error(`Failed to evaluate expression: ${expression}`, error);
      throw error;
    }
  }

  private static parseValue(valueStr: string): ModelValue {
    const lowerValue = valueStr.toLowerCase();

    // Handle boolean values
    if (lowerValue === "true") {
      return true;
    }
    if (lowerValue === "false") {
      return false;
    }

    // Handle null and undefined
    if (lowerValue === "null") {
      return null;
    }
    if (lowerValue === "undefined") {
      return undefined;
    }

    // Handle numbers
    if (!isNaN(Number(valueStr))) {
      return Number(valueStr);
    }

    // Handle strings - remove quotes if present
    return valueStr.replace(/^["']|["']$/g, "");
  }

  private static setValueByPath(obj: any, path: string, value: ModelValue): void {
    const parts = path.split(".");
    let current = obj;

    for (let i = 0; i < parts.length - 1; i++) {
      const part = parts[i];
      if (!(part in current)) {
        current[part] = {};
      }
      current = current[part];
    }

    current[parts[parts.length - 1]] = value;
  }
}
